import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { SERVICE_OPTIONS } from "@/lib/data";
import { submitDemo } from "@/lib/api";
import { useDemoModal } from "@/components/DemoModalContext";

const EMPTY = { name: "", company: "", phone: "", email: "", service: "", message: "" };

export default function DemoModal() {
  const { open, setOpen } = useDemoModal();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Lütfen ad, telefon ve e-posta alanlarını doldurun.");
      return;
    }
    setLoading(true);
    try {
      await submitDemo(form);
      toast.success("Demo talebiniz alındı! Ekibimiz sizinle iletişime geçecek.");
      setForm(EMPTY);
      setOpen(false);
    } catch {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-ink/10 bg-white px-4 py-3 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ink/35 focus:border-orange focus:ring-4 focus:ring-orange/10";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          data-testid="demo-modal"
        >
          <div
            className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-7 shadow-[0_40px_100px_rgba(0,0,0,0.25)] md:p-9"
            data-lenis-prevent
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Kapat"
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-cloud text-ink/60 transition-colors hover:bg-ink/10"
              data-testid="demo-modal-close"
            >
              <X className="h-4.5 w-4.5" />
            </button>

            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-orange">
              <Sparkles className="h-3.5 w-3.5" /> Ücretsiz Demo
            </span>
            <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-ink">
              Demo Talebi Oluştur
            </h3>
            <p className="mt-2 text-sm text-ink/55">
              Bilgilerinizi bırakın, uzman ekibimiz size özel demo için ulaşsın.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="demo-form">
              <input
                className={inputCls}
                placeholder="Ad Soyad *"
                value={form.name}
                onChange={update("name")}
                data-testid="demo-name"
              />
              <input
                className={inputCls}
                placeholder="Firma Adı"
                value={form.company}
                onChange={update("company")}
                data-testid="demo-company"
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  className={inputCls}
                  placeholder="Telefon *"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="demo-phone"
                />
                <input
                  className={inputCls}
                  type="email"
                  placeholder="E-posta *"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="demo-email"
                />
              </div>
              <select
                className={`${inputCls} ${form.service ? "text-ink" : "text-ink/35"}`}
                value={form.service}
                onChange={update("service")}
                data-testid="demo-service"
              >
                <option value="">İlgilendiğiniz Çözüm</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s} className="text-ink">
                    {s}
                  </option>
                ))}
              </select>
              <textarea
                className={`${inputCls} min-h-[90px] resize-none`}
                placeholder="Eklemek istedikleriniz"
                value={form.message}
                onChange={update("message")}
                data-testid="demo-message"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-70"
                data-testid="demo-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? "Gönderiliyor..." : "Talebi Gönder"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
